import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { Redirect, Route, Switch } from 'react-router';
import { NavRibbon, NavRibbonLink } from '../../../../components/nav-ribbon/NavRibbon';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PageHeading } from '../../../../components/page-components/PageHeading';
import { useRouteMiss } from '../../../../hooks/use-route-miss';
import { DealerSvc } from '../../../../shared/services/dealer-service';
import { StatusCode } from '../../../../utils/api';
import { PageNames, Routes } from '../../../../utils/app-constants';
import { AppUtils } from '../../../../utils/app-utils';
import { navigateToPage } from '../../../../utils/history';
import * as actions from '../../../../support/actions';
import { DealerDetailVM } from '../../../../support/models/dealers/dealer-read';
import { DealerBasicInfoScene } from './DealerBasicInfo';
import { DealerCreditsScene } from './DealerCredits';
import { DealerAddressesScene } from './DeliveryAddresses';
import { EditDeliveryAddress } from './EditDeliveryAddress';
import { AgingReport } from '../../../../modules/ordering/models/credit-hold-aging-report';
import { OrderingSvc } from '../../../../modules/ordering/service';
import { AgingReportScene } from './AgingReport';
import { hasPermission } from '../../../../containers/authorization-helpers/has-permission';

// #region Component State and Props

interface IProps extends IBaseSceneProps {
    // Event Handles
    searchOrder : any;
    getOrder    : any;
    clearDealerSearch : any;
    showLoading : any;
    hideLoading : any;
    dispatch    : any;
}

// #endregion

export const DealerDetailScene = (props: IProps) => {

    const [service] = React.useState(new DealerSvc());
    const [orderingService] = React.useState(new OrderingSvc());
    const [pageVM, setPageVM] = React.useState({} as DealerDetailVM);
    const [agingReport, setAgingReport] = React.useState<AgingReport | undefined>(undefined);
    const previousPath = React.useRef(props.location.pathname);

    async function getDealer(dealerId : string) {

        if (dealerId) {
            const result = await service.getFullDealer(dealerId);
            if (result.status === StatusCode.Success) {
                setPageVM(result.data);
                return;
            }
        }
        else {
            navigateToPage(Routes.SUPPORT_DEALERS);
        }
    }

    async function getAgingReport(dealerId: string) {
        const response = await orderingService.getAgingReport(dealerId);
        if (response.status === StatusCode.Success) {
            setAgingReport(response.data);
        }
    }

    function getDealerId() {
        const opts = props.location.pathname.split('/');
        return opts[3];
    }

    React.useEffect(() => {
        getDealer(getDealerId());

        const agingPerm = Permissions.SupportDealersView;
        if (hasPermission({ requiredPermissions: [agingPerm] })) {
            getAgingReport(getDealerId());
        }        
    }, []);

    // Load addresses again when the user came from the address create page
    React.useEffect(() => {
        const currentPath = props.location.pathname;
        const cameFromCreatePage = previousPath.current.endsWith('/addresses/create') && currentPath.endsWith('/addresses');
        if (cameFromCreatePage) {
            getDealer(getDealerId());
        }

        previousPath.current = currentPath;
    }, [props.location.pathname]);

    useRouteMiss(
        Routes.SUPPORT_DEALERS,
        actions.clearDealerSearch()
    );

    return !AppUtils.IsEmpty(pageVM) ?
        <PageContent pageName={PageNames.SUPPORT_DEALERS_DETAILS}>
            <PageHeading title={ pageVM.dealer.name }>
                <h1>{ pageVM.dealer.serviceCenterId }</h1>
            </PageHeading>

            <NavRibbon links={[
                new NavRibbonLink(
                    `/support/dealers/${ pageVM.dealer.id }/info`, 
                    'la-calendar-o',
                    'Basic Info',
                    [Permissions.SupportDealersView]
                ),
                new NavRibbonLink(
                    `/support/dealers/${ pageVM.dealer.id }/credits`, 
                    'la-money',
                    'Credits',
                    [Permissions.SupportDealersView]
                ),                
                new NavRibbonLink(
                    `/support/dealers/${ pageVM.dealer.id }/addresses`, 
                    'la-calendar-o',
                    'Delivery Address',
                    [Permissions.SupportDealersView]
                ),
                new NavRibbonLink(
                    `/support/dealers/${ pageVM.dealer.id }/aging`, 
                    'la-dollar',
                    'Aging Report',
                    [Permissions.SupportDealersView]
                )
            ]} />

            <Switch>

                <Route
                    key="support-dealers-info"
                    exact={true}
                    path="/support/dealers/:id/info"
                    render={() => <DealerBasicInfoScene dealer={pageVM.dealer} addresses={pageVM.addresses} />}
                />

                <Route
                    key="support-dealers-credit"
                    exact={true}
                    path="/support/dealers/:id/credits"
                    render={() => <DealerCreditsScene dealer={ pageVM.dealer } />}
                />                

                <Route
                    key="support-dealers-addresses"
                    exact={true}
                    path="/support/dealers/:id/addresses"
                    render={() => <DealerAddressesScene addresses={ pageVM.addresses } dealerId={ pageVM.dealer.id } />}
                />

                <Route
                    key="support-dealers-addresses-create"
                    exact={true}
                    path="/support/dealers/:id/addresses/create"
                    component={EditDeliveryAddress}
                />

                <Route
                    key="support-dealers-addresses-edit"
                    exact={true}
                    path="/support/dealers/:id/addresses/:addressId"
                    component={EditDeliveryAddress}
                />
				
                <Route
                    key="support-dealers-aging"
                    exact={true}
                    path="/support/dealers/:id/aging"
                    render={() => <AgingReportScene report={agingReport} dealerId = { getDealerId()} />}
                />

                <Redirect
                    key="production-manage-redirect"
                    exact={true}
                    from="/support/dealers/:id"
                    to={`/support/dealers/${ pageVM.dealer.id }/info`} />

            </Switch>
            
    </PageContent> : null;

};
